import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import {
  useGetUsersQuery,
  useUpdateUserMutation,
} from '../../slices/usersApiSlice';
import { useGetAllLoginAuditsQuery } from '../../slices/userLoginAuditApiSlice';
import { toast } from 'react-toastify';

import { DataGrid, GridToolbar, gridClasses } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import { green, blue, orange, grey, red } from '@mui/material/colors';
import Box from '@mui/material/Box';
import {
  Collapse,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Button,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import KeyboardArrowDownIcon  from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon    from '@mui/icons-material/KeyboardArrowUp';
import PersonOffIcon          from '@mui/icons-material/PersonOff';
import EditIcon               from '@mui/icons-material/Edit';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon     from '@mui/icons-material/CancelOutlined';
import PeopleIcon             from '@mui/icons-material/People';
import PersonIcon             from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import HourglassEmptyIcon     from '@mui/icons-material/HourglassEmpty';
import BlockIcon              from '@mui/icons-material/Block';
import { useNavigate } from 'react-router-dom';
import { useLayout } from '../../context/LayoutContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// ─── Styled DataGrid — light theme matching ShiftCompletedListScreen ──────────
const StyledDataGrid = styled(DataGrid)`
  & .${gridClasses.row}.activeRow {
    background-color: ${green[50]};
  }
  & .${gridClasses.row}.adminRow {
    background-color: ${blue[50]};
    border-left: 3px solid ${blue[400]};
  }
  & .${gridClasses.row}.inactiveRow {
    background-color: ${grey[100]};
    opacity: 0.75;
  }
  & .${gridClasses.row}.pendingRow {
    background-color: ${orange[50]};
  }
`;

// ─── LoginHistoryPanel ────────────────────────────────────────────────────────
const LoginHistoryPanel = ({ userId, loginAudits }) => {
  const [open, setOpen] = useState(false);
  const logs = loginAudits?.[String(userId)] || [];

  return (
    <Box sx={{ width: '100%' }}>
      <Box
  onClick={() => setOpen(!open)}
  sx={{
    display: 'flex', alignItems: 'center', gap: 0.5,
    cursor: 'pointer', userSelect: 'none',
    color: logs.length > 0 ? 'primary.main' : 'text.disabled',
    fontSize: '0.75rem',
    '&:hover': { color: 'text.primary' },
  }}
>
  <IconButton size="small" sx={{ color: 'inherit', p: 0.25 }}>
    {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
  </IconButton>
  {logs.length > 0 ? (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'primary.main', lineHeight: 1.3 }}>
        {logs.length} event{logs.length !== 1 ? 's' : ''} recorded
      </Typography>
      <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', lineHeight: 1.3 }}>
        Last: {fmtDate(logs[0].loginTime)}
      </Typography>
    </Box>
  ) : (
    'No history'
  )}
</Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Paper elevation={1} sx={{ mt: 0.5, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
          {logs.length === 0 ? (
            <Box sx={{ p: 1.5 }}>
              <Typography variant="body2" color="text.secondary">No history found.</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: grey[100] }}>
                  {['Time', 'Event', 'IP Address', 'Browser / Device', 'Note'].map(h => (
                    <TableCell key={h} sx={{
                      color: 'text.secondary', fontSize: '0.65rem',
                      textTransform: 'uppercase', letterSpacing: 1,
                      whiteSpace: 'nowrap', py: 0.75, fontWeight: 600,
                    }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log, idx) => {
                  const et = log.eventType || 'login';
                  const eventConfig = {
                    login:         { label: 'Login',       color: 'success' },
                    logout_manual: { label: 'Logout',      color: 'info' },
                    logout_auto:   { label: 'Auto Logout', color: 'warning' },
                  }[et] || { label: et, color: 'default' };
                  const isFailure = log.success === false;

                  return (
                    <TableRow key={log._id || idx} sx={{
                      '&:last-child td': { border: 0 },
                      bgcolor: idx === 0 ? blue[50] : 'transparent',
                      '&:hover': { bgcolor: grey[50] },
                    }}>
                      <TableCell sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                        {fmtDate(log.loginTime)}
                        {idx === 0 && (
                          <Chip label="Latest" size="small" sx={{ ml: 0.75, height: 16, fontSize: '0.6rem' }} color="primary" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isFailure ? 'Failed Login' : eventConfig.label}
                          size="small"
                          color={isFailure ? 'error' : eventConfig.color}
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.62rem', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                        {log.ipAddress || '—'}
                      </TableCell>
                      <TableCell sx={{
                        color: 'text.secondary', fontSize: '0.68rem',
                        maxWidth: 240, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        <span
                          data-tooltip-id={`ua-${log._id}`}
                          data-tooltip-content={log.userAgent || ''}
                          style={{ cursor: 'default' }}
                        >
                          {log.userAgent
                            ? log.userAgent.length > 50
                              ? log.userAgent.substring(0, 48) + '…'
                              : log.userAgent
                            : '—'}
                        </span>
                        <ReactTooltip id={`ua-${log._id}`} place="top" />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                        {log.note || '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Collapse>
    </Box>
  );
};

// ─── Main Screen ──────────────────────────────────────────────────────────────
const UserListScreen = () => {
  const { data: users = [], refetch, isLoading, error } = useGetUsersQuery();
  const { data: loginAudits = {} } = useGetAllLoginAuditsQuery();
  const [updateUser] = useUpdateUserMutation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { userInfo } = useSelector((state) => state.auth);
  const { setFluid } = useLayout();
  const navigate = useNavigate();
  const [userFilter, setUserFilter] = useState('active');

  useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  let filteredUsers = [];
  if (users && Array.isArray(users)) {
    const usersCopy = [...users];
    filteredUsers = (!userInfo.isRadAdmin
      ? usersCopy.filter(u => !u.isRadAdmin)
      : usersCopy
    ).sort((a, b) => a.name.localeCompare(b.name));
  }

  // ── Soft-delete ───────────────────────────────────────────────────────────
  const deactivateHandler = async (id, name) => {
    if (window.confirm(`Deactivate user "${name}"? They will no longer be able to log in.`)) {
      try {
        await updateUser({ userId: id, isActive: false }).unwrap();
        toast.success(`${name} has been deactivated`);
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error || 'Failed to deactivate user');
      }
    }
  };

  const renderErrorOrLoading = () => {
    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><Loader /></Box>;
    if (error) return <Message variant="danger">{error?.data?.message || error.error}</Message>;
    return null;
  };

  // ── Stats ─────────────────────────────────────────────────────────────────
  const totalUsers    = filteredUsers.length;
  const activeUsers   = filteredUsers.filter(u => u.isActive !== false && u.isApproved).length;
  const inactiveUsers = filteredUsers.filter(u => u.isActive === false).length;
  const pendingUsers  = filteredUsers.filter(u => u.isActive !== false && !u.isApproved).length;
  const adminUsers    = filteredUsers.filter(u => u.isAdmin).length;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const loggedInToday = filteredUsers.filter(u => {
  const logs = loginAudits?.[String(u._id)];
  if (!logs || logs.length === 0) return false;
  return logs.some(log =>
    log.success !== false &&
    new Date(log.loginTime) >= today
  );
  }).length;

  // ── Displayed users ───────────────────────────────────────────────────────
  const displayedUsers = filteredUsers.filter(u => {
    switch (userFilter) {
      case 'active':   return u.isActive !== false && u.isApproved;
      case 'inactive': return u.isActive === false;
      case 'pending':  return u.isActive !== false && !u.isApproved;
      case 'admin':    return u.isAdmin === true;
      default:         return true;
    }
  });

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    {
  field: 'name',
  headerName: 'Name',
  width: 210,
  renderCell: (params) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
          {params.value}
        </Typography>
        {params.row.wardExclusions?.length > 0 && (
          <Chip
            label={`${params.row.wardExclusions.length} excl.`}
            size="small"
            color="error"
            variant="outlined"
            sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700 }}
          />
        )}
      </Box>
      {params.row.speciality && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
          {params.row.speciality}
        </Typography>
      )}
    </Box>
  ),
},
    {
      field: 'email',
      headerName: 'Email',
      width: 230,
      renderCell: (params) => (
        <Typography variant="body2" color="primary" sx={{ fontSize: '0.75rem' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'annualLeave',
      headerName: 'Annual Leave',
      width: 130,
      type: 'number',
      renderCell: (params) => (
        <Box
          data-tooltip-id="leave-tooltip"
          data-tooltip-html={
            `Opening Balance: ${params.row.openingBalance?.toFixed(2) || '0.00'} hrs<br/>` +
            `Bring Forward: ${params.row.bringForward?.toFixed(2) || '0.00'} hrs`
          }
        >
          <Typography variant="body2" sx={{
            fontSize: '0.8rem',
            color: params.value > 200 ? green[700] : params.value < 0 ? red[700] : 'text.primary',
            fontWeight: (params.value > 200 || params.value < 0) ? 600 : 400,
          }}>
            {(params.value || 0).toFixed(2)} hrs
          </Typography>
        </Box>
      ),
    },
    {
      field: 'isAdmin',
      headerName: 'Admin',
      width: 75,
      renderCell: (params) => params.value
        ? <CheckCircleOutlineIcon sx={{ color: blue[600], fontSize: 20 }} />
        : <CancelOutlinedIcon    sx={{ color: grey[300], fontSize: 20 }} />,
    },
    {
      field: 'areyouaclinicaldirector',
      headerName: 'Manager',
      width: 85,
      renderCell: (params) => params.value
        ? <CheckCircleOutlineIcon sx={{ color: green[600], fontSize: 20 }} />
        : <CancelOutlinedIcon    sx={{ color: grey[300], fontSize: 20 }} />,
    },
    {
      field: 'isApproved',
      headerName: 'Approved',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Approved' : 'Pending'}
          size="small"
          color={params.value ? 'success' : 'warning'}
          variant="outlined"
          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 95,
      renderCell: (params) => (
        <Chip
          label={params.value !== false ? 'Active' : 'Inactive'}
          size="small"
          color={params.value !== false ? 'success' : 'error'}
          variant="filled"
          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'loginHistory',
      headerName: 'Login History',
      width: 340,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <LoginHistoryPanel userId={params.row.id} loginAudits={loginAudits} />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<EditIcon sx={{ fontSize: 13 }} />}
            onClick={() => navigate(`/admin/user/${params.row.id}/edit`)}
            sx={{ fontSize: '0.7rem', py: 0.4, px: 1.2 }}
          >
            Edit
          </Button>

          <Tooltip title={params.row.isActive !== false ? 'Deactivate user' : 'Already inactive'}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<PersonOffIcon sx={{ fontSize: 13 }} />}
                disabled={params.row.isActive === false}
                onClick={() => deactivateHandler(params.row.id, params.row.name)}
                sx={{ fontSize: '0.7rem', py: 0.4, px: 1.2 }}
              >
                {params.row.isActive === false ? 'Inactive' : 'Deactivate'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // ── Row class ─────────────────────────────────────────────────────────────
  const getRowClassName = (params) => {
    if (params.row.isActive === false) return 'inactiveRow';
    if (params.row.isAdmin)            return 'adminRow';
    if (!params.row.isApproved)        return 'pendingRow';
    return 'activeRow';
  };

  const getRowSpacing = useCallback((params) => ({
    top:    params.isFirstVisible ? 0 : 1,
    bottom: params.isLastVisible  ? 0 : 1,
  }), []);

  // ── Rows ──────────────────────────────────────────────────────────────────
  const rows = displayedUsers.map((user) => ({
    id:                      user._id,
    speciality:              user.speciality || '',
    name:                    user.name,
    email:                   user.email,
    wardExclusions:          user.wardExclusions || [],   // ← add this
    openingBalance:          user.openingBalance || 0,
    bringForward:            user.bringForward   || 0,
    annualLeave:             (user.openingBalance || 0) + (user.bringForward || 0),
    isAdmin:                 user.isAdmin,
    areyouaclinicaldirector: user.areyouaclinicaldirector,
    isApproved:              user.isApproved,
    isActive:                user.isActive,
    isLocum:                 user.isLocum,
    locumEndDate:            user.locumEndDate,
  }));

  // ── Mobile cards ──────────────────────────────────────────────────────────
  const renderUsersCards = () => (
    <Box sx={{ p: 2 }}>
      {displayedUsers.map((user) => {
        const annualLeave = (user.openingBalance || 0) + (user.bringForward || 0);
        const logs = loginAudits?.[String(user._id)] || [];
        const isInactive = user.isActive === false;

        return (
          <Paper key={user._id} elevation={1} sx={{
            mb: 2, border: '1px solid #e0e0e0', borderRadius: 2, p: 2,
            bgcolor: isInactive ? grey[100] : 'white',
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{user.name}</Typography>
                <Typography variant="body2" color="primary" sx={{ fontSize: '0.75rem' }}>{user.email}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {user.isAdmin && <Chip label="Admin" size="small" color="info" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />}
                <Chip
                  label={isInactive ? 'Inactive' : 'Active'}
                  size="small"
                  color={isInactive ? 'error' : 'success'}
                  sx={{ fontSize: '0.6rem', height: 20 }}
                />
              </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Annual Leave: {annualLeave.toFixed(2)} hrs
            </Typography>

            {logs.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Last seen: {fmtDate(logs[0].loginTime)}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button size="small" variant="outlined" color="primary" startIcon={<EditIcon sx={{ fontSize: 13 }} />}
                onClick={() => navigate(`/admin/user/${user._id}/edit`)}>
                Edit
              </Button>
              {!isInactive && (
                <Button size="small" variant="outlined" color="error" startIcon={<PersonOffIcon sx={{ fontSize: 13 }} />}
                  onClick={() => deactivateHandler(user._id, user.name)}>
                  Deactivate
                </Button>
              )}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );

  // ── Desktop table ─────────────────────────────────────────────────────────
  const renderUsersTable = () => (
    <Box sx={{ height: 750, width: '100%' }}>
      <StyledDataGrid
        density="compact"
        rows={rows}
        columns={columns}
        getRowClassName={getRowClassName}
        getRowSpacing={getRowSpacing}
        getRowHeight={() => 'auto'}
        slots={{ toolbar: GridToolbar }}
        slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 } } }}
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { pageSize: 25 } }}
        checkboxSelection
        disableRowSelectionOnClick
      />
    </Box>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ height: 'calc(100vh - 100px)', width: '100%', p: 2 }}>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <PeopleIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h4">User Management</Typography>
          </Box>
          <Typography variant="subtitle2" color="text.secondary">
            Beaumont Hospital · Admin Panel
          </Typography>
        </Box>

        <Button variant="contained" color="primary" onClick={() => refetch()}>
          Refresh Data
        </Button>
      </Box>

      {/* ── Stats cards ───────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Users',    value: totalUsers,    color: grey[700],    icon: <PeopleIcon /> },
          { label: 'Active',         value: activeUsers,   color: green[600],   icon: <PersonIcon /> },
          { label: 'Inactive',       value: inactiveUsers, color: red[600],     icon: <BlockIcon /> },
          { label: 'Pending',        value: pendingUsers,  color: orange[700],  icon: <HourglassEmptyIcon /> },
          { label: 'Admins',         value: adminUsers,    color: blue[600],    icon: <AdminPanelSettingsIcon /> },
          { label: 'Logged in Today', value: loggedInToday, color: blue[700], icon: <CheckCircleOutlineIcon /> },
        ].map(({ label, value, color, icon }) => (
          <Grid item xs={6} sm={4} md={2} key={label}>
            <Card elevation={1} sx={{ border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: '12px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box sx={{ color, display: 'flex' }}>{React.cloneElement(icon, { sx: { fontSize: 20 } })}</Box>
                  <Typography variant="h5" sx={{ color, fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>{label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Filter tabs ───────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {[
          { key: 'active',   label: 'Active',   count: activeUsers,   color: green[700],  bg: green[50]  },
          { key: 'inactive', label: 'Inactive', count: inactiveUsers, color: red[700],    bg: red[50]    },
          { key: 'pending',  label: 'Pending',  count: pendingUsers,  color: orange[800], bg: orange[50] },
          { key: 'admin',    label: 'Admins',   count: adminUsers,    color: blue[700],   bg: blue[50]   },
        ].map(({ key, label, count, color, bg }) => (
          <Box
            key={key}
            onClick={() => setUserFilter(key)}
            sx={{
              px: 2, py: 0.75, borderRadius: 1, cursor: 'pointer',
              bgcolor: userFilter === key ? bg : 'transparent',
              border: `1px solid ${userFilter === key ? color : '#bdbdbd'}`,
              color: userFilter === key ? color : 'text.secondary',
              fontSize: '0.78rem', fontWeight: userFilter === key ? 700 : 400,
              transition: 'all 0.15s',
              '&:hover': { bgcolor: bg, borderColor: color, color },
            }}
          >
            {label} ({count})
          </Box>
        ))}
      </Box>

      {/* ── Row legend ────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { label: 'Active user',   bg: green[50],  border: green[300] },
          { label: 'Admin user',    bg: blue[50],   border: blue[400], leftBorder: true },
          { label: 'Pending user',  bg: orange[50], border: orange[300] },
          { label: 'Inactive user', bg: grey[100],  border: grey[400] },
        ].map(({ label, bg, border, leftBorder }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{
              width: 16, height: 16, borderRadius: 0.5, bgcolor: bg,
              border: `1px solid ${border}`,
              borderLeft: leftBorder ? `3px solid ${border}` : undefined,
            }} />
            <Typography variant="caption" color="text.secondary">{label}</Typography>
          </Box>
        ))}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          · Deactivate sets the user as inactive — no data is deleted
        </Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {renderErrorOrLoading()}

      {windowWidth < 900 ? renderUsersCards() : renderUsersTable()}

      <ReactTooltip id="leave-tooltip" place="top" />
    </Box>
  );
};

export default UserListScreen;
